import { useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { toast } from "sonner";
import { Pencil, Copy, FileDown, FileText, Save, X } from "lucide-react";
import api from "@/lib/api";
import AiDisclaimer from "@/components/AiDisclaimer";

const slug = (s) => (s || "notes").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "notes";

export default function NotesPanel({ sc, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sc.notes_markdown || "");
  const [saving, setSaving] = useState(false);
  const articleRef = useRef(null);
  const md = sc.notes_markdown || "";

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/subcourses/${sc.id}`, { notes_markdown: draft });
      toast.success("Notes saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save notes");
    } finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(md); toast.success("Notes copied to clipboard"); }
    catch { toast.error("Couldn't copy — your browser blocked clipboard access"); }
  };

  const exportMd = () => {
    const url = URL.createObjectURL(new Blob([md], { type: "text/markdown;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `${slug(sc.title)}.md` });
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to export a PDF");
    const title = (sc.title || "Notes").replace(/</g, "&lt;");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.css">
      <style>.katex-display{margin:14px 0;overflow-x:auto}body{font-family:Georgia,serif;max-width:720px;margin:40px auto;padding:0 24px;color:#111;line-height:1.6}
      h1,h2,h3{font-family:Helvetica,Arial,sans-serif}blockquote{border-left:3px solid #7C5CFF;margin:12px 0;padding:4px 14px;background:#f5f3ff}
      table{border-collapse:collapse}td,th{border:1px solid #ccc;padding:4px 8px}code{background:#f2f2f2;padding:1px 4px}
      footer{margin-top:40px;font-size:11px;color:#777}</style></head>
      <body><h1>${title}</h1>${articleRef.current?.innerHTML || ""}<footer>Exported from Studivault · studivault.com</footer></body></html>`);
    w.document.close();
    w.focus();
    // Wait for the KaTeX stylesheet + fonts so formulas print correctly.
    const doPrint = () => setTimeout(() => w.print(), 250);
    const link = w.document.querySelector("link[rel=stylesheet]");
    if (link && !link.sheet) { link.onload = doPrint; link.onerror = doPrint; } else doPrint();
  };

  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] border border-white/10 hover:border-[#B8A4FF]/50 hover:text-[#B8A4FF] transition-colors";

  return (
    <div className="mx-auto" style={{ maxWidth: "72ch" }}>
      <div className="flex flex-wrap justify-end gap-2 mb-5" data-testid="notes-toolbar">
        {editing ? (
          <>
            <button onClick={() => { setEditing(false); setDraft(md); }} className={btn} disabled={saving} data-testid="notes-cancel-btn"><X size={13} /> Cancel</button>
            <button onClick={save} className="btn btn-primary px-4 py-1.5 text-[12.5px]" disabled={saving} data-testid="notes-save-btn"><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
          </>
        ) : (
          <>
            <button onClick={() => { setDraft(md); setEditing(true); }} className={btn} data-testid="notes-edit-btn"><Pencil size={13} /> Edit</button>
            <button onClick={copy} className={btn} data-testid="notes-copy-btn"><Copy size={13} /> Copy</button>
            <button onClick={exportMd} className={btn} data-testid="notes-export-md-btn"><FileText size={13} /> Markdown</button>
            <button onClick={exportPdf} className={btn} data-testid="notes-export-pdf-btn"><FileDown size={13} /> PDF</button>
          </>
        )}
      </div>
      {editing ? (
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="input font-mono text-[13px] leading-relaxed w-full"
          style={{ minHeight: "60vh" }} aria-label="Edit notes (Markdown)" data-testid="notes-editor" />
      ) : (
        <article className="prose-apple mx-auto">
          <div ref={articleRef}><ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{md || "No notes."}</ReactMarkdown></div>
          <AiDisclaimer className="mt-8 not-prose" />
        </article>
      )}
    </div>
  );
}
